import { useCallback, useMemo } from 'react'
import { Copy, t } from '@stokr/components-library'
import type { CopyKey } from 'locales/copy-keys.gen'
import { AXIS_CHART_HEIGHT, ChartFootnote, MinedSourceNote, RangePicker } from 'components/sto/chart'
import { PriceChart, chartColor, formatChartCompact, formatChartMoney } from 'components/ui/chart'
import { useChartRange } from 'hooks/sto/useChartRange'
import { latestDate, redeemedOn, type HistoryColumn, type ProductChartView } from 'models/productChart'
import { DownloadCsv } from './PriceHistory'
import {
  ChartArea,
  ChartCard,
  ChartHeader,
  Heading,
  HeadingTitle,
  Legend,
  LegendDot,
  Legends,
  Stack,
} from './Performance.styles'
import { useChartHoverTracking } from 'hooks/sto/useStoTracking'

/** A mining note's two tabs: the BTC mined so far, and the issuance amount. */
export type MiningNoteView = 'mined' | 'issuance'

const COPY: Record<MiningNoteView, { title: CopyKey; legend: CopyKey; header: CopyKey }> = {
  mined: {
    title: 'stoPage.performance.seriesTitle',
    legend: 'stoPage.performance.legendSeries',
    header: 'stoPage.performance.valueHeader',
  },
  issuance: {
    title: 'stoPage.performance.issuanceTitle',
    legend: 'stoPage.performance.legendIssuance',
    header: 'stoPage.performance.issuanceHeader',
  },
}

/** Matured note (BMN1): the mined value against holding the nominal in BTC, as production shows it. */
const BUY_AND_HOLD_COPY = {
  title: 'stoPage.performance.buyHoldTitle',
  legend: 'stoPage.performance.legendNoteValue',
  header: 'stoPage.performance.noteValueHeader',
} as const satisfies (typeof COPY)[MiningNoteView]

/** Fully redeemed note charted from its supply (PKB2): the same chart, told as a finished story. */
const REDEEMED_COPY = {
  title: 'stoPage.performance.issuanceRedeemedTitle',
  legend: 'stoPage.performance.legendIssuance',
  header: 'stoPage.performance.issuanceHeader',
} as const satisfies (typeof COPY)[MiningNoteView]

/**
 * Mining note (Figma 3362:83661, BMN2): one series per note with value and date axes, over its
 * whole life, in brand blue. `issuance` is the first tab of a note still running; a matured
 * note (BMN1) adds a red buy-and-hold line to compare. A note without mining data (PKH2) has only
 * its issuance, from its supply.
 */
export const MiningNotePerformance = ({ chart, view }: ProductChartView & { view: MiningNoteView }) => {
  const [{ currency: minedCurrency, points: mined }] = chart.denominations
  // BMN2 charts the BTC mined in BTC; its issuance amount stays in the note's currency.
  const currency = view === 'issuance' ? (chart.note?.issuanceCurrency ?? minedCurrency) : minedCurrency
  // Fully redeemed (PKB2): its history, up to the day the last notes went, without the zeros after.
  const redeemed = view === 'issuance' ? redeemedOn(chart) : undefined
  const all = useMemo(() => {
    const points = view === 'issuance' ? (chart.note?.issuance ?? []) : mined
    return redeemed === undefined ? points : points.filter(([time]) => time <= redeemed)
  }, [view, chart.note?.issuance, mined, redeemed])
  const buyAndHold = view === 'mined' ? chart.note?.buyAndHold : undefined
  // Charted from its supply: its issuance is not BTC-based, so it says so.
  const supply = view === 'issuance' ? chart.note?.supply : undefined
  const copy = buyAndHold ? BUY_AND_HOLD_COPY : supply && redeemed !== undefined ? REDEEMED_COPY : COPY[view]
  const range = useChartRange(all)
  const trackHover = useChartHoverTracking('performance')
  // Stable, so re-renders of the section do not hand the chart a new formatter to redraw with.
  const formatMoney = useCallback((value: number) => formatChartMoney(value, currency), [currency])
  // The comparison line over the same range as the main one.
  const comparePoints = useMemo(() => {
    if (!buyAndHold) return undefined
    const from = range.points[0]?.[0] ?? -Infinity
    return buyAndHold.points.filter(([time]) => time >= from)
  }, [buyAndHold, range.points])
  const compare = useMemo(
    () =>
      comparePoints && {
        points: comparePoints,
        color: chartColor.negative,
        name: t('stoPage.performance.legendBuyHold', { currency }),
      },
    [comparePoints, currency],
  )
  // The CSV is this tab's series (and the comparison, side by side).
  const history = useMemo<HistoryColumn[]>(
    () => [
      { currency, headerKey: copy.header, points: all },
      ...(buyAndHold
        ? [{ currency, headerKey: 'stoPage.performance.buyHoldHeader' as const, points: buyAndHold.points }]
        : []),
    ],
    [currency, copy.header, all, buyAndHold],
  )
  const values = { symbol: chart.symbol, currency }
  const showSource = view === 'mined' && chart.symbol === 'BMN2'

  return (
    <Stack>
      <Heading>
        <HeadingTitle>
          <Copy id={copy.title} values={values} />
        </HeadingTitle>
      </Heading>

      <ChartCard>
        <ChartArea>
          <ChartHeader>
            <Legends>
              <Legend>
                <LegendDot $color={chartColor.fund} aria-hidden="true" />
                <Copy id={copy.legend} values={values} />
              </Legend>
              {compare && (
                <Legend>
                  <LegendDot $color={compare.color} aria-hidden="true" />
                  {compare.name}
                </Legend>
              )}
            </Legends>
            <RangePicker range={range} chart="performance" placement="header" />
          </ChartHeader>

          <PriceChart
            points={range.points}
            color={chartColor.fund}
            height={AXIS_CHART_HEIGHT}
            showAxes
            denseValueAxis={currency === 'BTC'}
            formatValue={formatChartCompact}
            formatTooltipValue={formatMoney}
            onHover={trackHover}
            name={t(copy.legend, values)}
            compare={compare}
            description={t(copy.title, values)}
          />
          <RangePicker range={range} chart="performance" placement="below" />
        </ChartArea>
      </ChartCard>

      <ChartFootnote asOf={latestDate(all) ?? chart.asOf} source={chart.source}>
        <DownloadCsv history={history} symbol={chart.symbol} />
      </ChartFootnote>
      {showSource && <MinedSourceNote />}
    </Stack>
  )
}
